import { ref } from "vue";
import { defineStore } from "pinia";
import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";

export interface UserRecord {
  uuid: string;
  [key: string]: unknown;
}

export interface UserFormData {
  name?: string;
  email?: string;
  password?: string;
  roles?: string[];
}

export interface UpdateUserPayload {
  uuid: string;
  formData: UserFormData;
}

export interface UpdatePasswordPayload {
  uuid?: string;
  formData: { password: string };
}

export interface GetUserListPayload {
  organisation_uuid: string;
  params?: Record<string, string | number | boolean>;
}

export interface GetUsersByRolePayload {
  organisation_uuid: string;
  role: string;
}

export interface CreateUserPayload {
  uuid: string;
  formData: UserFormData;
}

export interface DeleteUsersPayload {
  users: { uuid: string }[];
}

export interface FetchAntispoofUsersPayload {
  uuid: string | string[] | null | undefined;
  params?: Record<string, string | number | boolean>;
}

export interface AzureUsersPayload {
  id: string | null | undefined;
  filter: string;
}

export const useUsersStore = defineStore("users", () => {
  const users = ref<UserRecord[]>([]);
  const usersTotal = ref(0);
  const selectedUser = ref<UserRecord | null>(null);
  const azureUsers = ref<unknown[]>([]);
  const azureUsersTotal = ref(0);
  const antispoofUsers = ref<unknown[]>([]);
  const antispoofUsersTotal = ref(0);

  const getUserList = async ({
    organisation_uuid,
    params = {},
  }: GetUserListPayload): Promise<void> => {
    const url = new URL(`${getApiClientUrl()}/v1/idp/users`);
    url.search = new URLSearchParams({
      organisation_uuid,
      ...(params as Record<string, string>),
    }).toString();

    const response = await $http.get(url.toString());

    users.value = response.data.data;
    usersTotal.value = response.data.total;
  };

  const getUsersByRole = async ({
    organisation_uuid,
    role,
  }: GetUsersByRolePayload): Promise<{ count: number }> => {
    const url = new URL(`${getApiClientUrl()}/v1/idp/users`);
    url.search = new URLSearchParams({
      organisation_uuid,
      filter: role,
      sortKey: "role",
    }).toString();

    const response = await $http.get(url.toString());

    return response.data;
  };

  const createUser = async ({ uuid, formData }: CreateUserPayload) => {
    const response = await $http.post(`${getApiClientUrl()}/v1/idp/users`, {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      organisation_uuid: uuid,
      roles: formData.roles || [],
    });

    users.value.push(response.data);

    return response;
  };

  const updateAdministratorUser = async ({
    uuid,
    formData,
  }: UpdateUserPayload): Promise<void> => {
    await $http.patch(`${getApiClientUrl()}/v1/idp/users/${uuid}`, {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      roles: formData.roles,
    });
  };

  const updateUserPassword = async ({
    uuid,
    formData,
  }: UpdatePasswordPayload): Promise<void> => {
    await $http.patch(`${getApiClientUrl()}/v1/idp/users/${uuid}/password`, {
      password: formData.password,
    });
  };

  const deleteUsers = async ({
    users: toDelete,
  }: DeleteUsersPayload): Promise<unknown> => {
    const response = await $http.delete(
      `${getApiClientUrl()}/v1/idp/users/${toDelete[0].uuid}`,
    );

    users.value = users.value.filter(
      (user) => user.uuid !== response.data.uuid,
    );

    return response;
  };

  const fetchAntispoofUsers = async ({
    uuid,
    params = {},
  }: FetchAntispoofUsersPayload): Promise<void> => {
    const url = new URL(`${getApiClientUrl()}/v1/antispoof/users`);
    const reqParams = new URLSearchParams(params as Record<string, string>);
    reqParams.set("organisation_uuid", String(uuid));
    url.search = reqParams.toString();

    const { data } = await $http.get(url.toString());

    antispoofUsers.value = data.data;
    antispoofUsersTotal.value = data.total;
  };

  const fetchAzureUsers = async ({
    id,
    filter,
  }: AzureUsersPayload): Promise<void> => {
    const response = await $http.get(
      `${getApiClientUrl()}/v1/pt/users?organisationID=${id}&top=999&filter=${filter}`,
      { _hasCustomErrorHandling: true },
    );

    azureUsers.value = response.data.users;
    azureUsersTotal.value = response.data.metadata?.total || 0;
  };

  const getUser = async ({ uuid }: { uuid: string }): Promise<UserRecord> => {
    const response = await $http.get(
      `${getApiClientUrl()}/v1/idp/users/${uuid}`,
    );

    selectedUser.value = response.data;

    return response.data;
  };

  const clearUsersCache = (): void => {
    users.value = [];
    usersTotal.value = 0;
    selectedUser.value = null;
    azureUsers.value = [];
    azureUsersTotal.value = 0;
    antispoofUsers.value = [];
    antispoofUsersTotal.value = 0;
  };

  return {
    users,
    usersTotal,
    selectedUser,
    azureUsers,
    azureUsersTotal,
    antispoofUsers,
    antispoofUsersTotal,
    getUserList,
    getUsersByRole,
    createUser,
    updateAdministratorUser,
    updateUserPassword,
    deleteUsers,
    fetchAntispoofUsers,
    fetchAzureUsers,
    getUser,
    clearUsersCache,
  };
});
